import React, { useContext } from "react";
import PropTypes from "prop-types";

import RenderItem from "../../RenderItem/RenderItem";
import calcCharging from "../../../functions/vehicle/Charging/calcCharging";
import calcEmissions from "./../../../functions/vehicle/Emissions/calcEmissions";

import {
  FormatAsThousands,
  FormatAsTime,
} from "./../../../utils/Helpers/Format";
import IconOil from "./../../../client_customizations/assets/images/icons/icon-barrel.png";
import UserPrefsContext from "../../../context/UserPrefs/UserPrefsContext";
import { useIntl } from "react-intl";

const CarDetails = ({ car, setUsedEv }) => {
  const intl = useIntl();
  const userPrefs = useContext(UserPrefsContext);
  const milesDrivenAnnually = userPrefs.get("milesDrivenAnnually");
  const electricMilesPortionForPhev = userPrefs.get(
    "electricMilesPortionForPhev"
  );

  const detailedObjects = [
    {
      title: intl.formatMessage({ id: "vehicle.type", defaultMessage: "TYPE" }),
      val: car["form_factor"],
    },
    {
      title: "SEATS",
      val: car["seats_min"],
    },
    {
      title: intl.formatMessage({
        id: "vehicle.fuelType",
        defaultMessage: "FUEL TYPE",
      }),
      val: car["fuel"],
    },
    {
      title: "ESTIMATED ELECTRIC RANGE",
      val: `${car["electric_range"]} ${
        process.env.REACT_APP_METRIC_SYSTEM
          ? intl.formatMessage({
              id: "vehicle.kilometersShort",
              defaultMessage: "km",
            })
          : intl.formatMessage({ id: "vehicle.miles", defaultMessage: "miles" })
      }`,
      tooltip: setUsedEv
        ? "To account for battery degradation, the range quoted here is an estimation based on the age of the vehicle and normal driving/charging patterns."
        : null,
    },
    {
      title: "ESTIMATED TOTAL RANGE",
      val: `${car["total_range"]} ${
        process.env.REACT_APP_METRIC_SYSTEM
          ? intl.formatMessage({
              id: "vehicle.kilometersShort",
              defaultMessage: "km",
            })
          : intl.formatMessage({ id: "vehicle.miles", defaultMessage: "miles" })
      }`,
    },
    {
      title: "TIME TO CHARGE - LEVEL 2",
      val:
        "~ " +
        FormatAsTime(
          calcCharging.timeForFullBattery(
            car.battery_capacity,
            car.ac_charging_power,
            "level_2"
          )
        ),
      tooltip:
        "Assumes an average mid-size Level 2 charger (7.7 kW). Note: Level 2 chargers require a 240V outlet. Level 1 charge times are much longer.",
    },
    {
      title: "ESTIMATED BATTERY SIZE",
      val: car["battery_capacity"] + " kWh",
      tooltip: setUsedEv
        ? "One kilowatt-hour is enough energy to drive 1 to 4 miles, depending on the vehicle. For reference, a typical laptop battery can hold less than 0.1 kWh"
        : "One kilowatt-hour is enough energy to drive 1 to 4 miles, depending on the vehicle. For reference, a typical laptop battery can hold less than 0.1 kWh",
    },
    {
      title: "CO2 EMISSION REDUCTION",
      val:
        FormatAsThousands(
          calcEmissions.emissionReduction(
            car,
            milesDrivenAnnually,
            electricMilesPortionForPhev,
            userPrefs
          )
        ) +
        " " +
        (process.env.REACT_APP_METRIC_SYSTEM
          ? intl.formatMessage({ id: "kgperyear", defaultMessage: "kg/yr" })
          : intl.formatMessage({ id: "lbsperyear", defaultMessage: "lbs/yr" })),
    },
    {
      title: "Gasoline Saved",
      val: (
        <span>
          <img alt="" src={IconOil} style={{ width: "15px" }} />{" "}
          {FormatAsThousands(
            calcEmissions.fuelSaved(
              car,
              milesDrivenAnnually,
              electricMilesPortionForPhev
            )
          )}{" "}
          {process.env.REACT_APP_METRIC_SYSTEM
            ? intl.formatMessage({ id: "literperyear", defaultMessage: "L/yr" })
            : intl.formatMessage({
                id: "galperyear",
                defaultMessage: "ga / yr",
              })}
        </span>
      ),
    },
    {
      title: "Trees Saved",
      val: (
        <span>
          {`${FormatAsThousands(
            calcEmissions.treesPlanted(
              car,
              milesDrivenAnnually,
              electricMilesPortionForPhev,
              userPrefs
            )
          )} trees / yr`}
        </span>
      ),
      tooltip: (
        <p>
          To express annual CO2 emission savings in terms of trees, we consider
          that trees continually absorb CO2 at the rate of 48 lbs per year, per
          the{" "}
          <a href="https://www.arborenvironmentalalliance.com/carbon-tree-facts.asp">
            Arbor Environmental Alliance
          </a>
          . Hence, a reduction of 48 lbs of CO2 emissions per year corresponds
          to one tree.
        </p>
      ),
    },
  ];

  let renderedCount = 0;
  let details = detailedObjects.map((obj) => {
    if (!obj.hide) renderedCount += 1;

    return obj.hide ? null : (
      <div className="col-6" key={renderedCount}>
        <RenderItem
          title={obj.title}
          value={obj.val}
          tooltipText={obj.tooltip}
        />
      </div>
    );
  });

  return <div className="row">{details}</div>;
};

export default CarDetails;

CarDetails.propTypes = {
  car: PropTypes.object,
};
